import React, { useState, useCallback } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Dimensions,
  Modal,
  TextInput,
  TouchableOpacity,
  Pressable,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import {
  DndContext,
  useDraggable,
  useDroppable,
  DragOverlay,
  closestCorners,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const { height: SCREEN_HEIGHT, width: SCREEN_WIDTH } = Dimensions.get("window");
const COLUMN_WIDTH = 300;

// Types
interface Task {
  id: string;
  title: string;
  description: string;
  priority: "low" | "medium" | "high" | "critical";
  assignee?: string;
  tags?: string[];
  orderNumber?: string;
  dueDate?: string;
}

interface Column {
  id: string;
  title: string;
  color: string;
  tasks: Task[];
}

// Helpers
const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "critical":
      return "#DC2626";
    case "high":
      return "#F59E0B";
    case "medium":
      return "#3B82F6";
    case "low":
      return "#10B981";
    default:
      return "#6B7280";
  }
};

const getPriorityIcon = (priority: string) => {
  switch (priority) {
    case "critical":
      return "alert-octagon";
    case "high":
      return "arrow-up-circle";
    case "medium":
      return "minus-circle";
    case "low":
      return "arrow-down-circle";
    default:
      return "circle-outline";
  }
};

const PRIORITY_OPTIONS = ["low", "medium", "high", "critical"];

// Draggable Card
function DraggableTaskCard({
  task,
  onPress,
  onDelete,
}: {
  task: Task;
  onPress: (task: Task) => void;
  onDelete: (taskId: string) => void;
}) {
  const { setNodeRef, listeners, attributes, transform, isDragging } =
    useDraggable({
      id: task.id,
    });

  const style = transform
    ? {
        transform: `translate(${transform.x}px, ${transform.y}px)`,
        zIndex: 1000,
      }
    : undefined;

  return (
    <View
      ref={setNodeRef}
      style={[
        styles.taskCard,
        isDragging && styles.taskCardDragging,
        style as any,
      ]}
      {...attributes}
      {...listeners}
    >
      {!isDragging && (
        <Pressable onPress={() => onPress(task)}>
          <View style={styles.taskHeader}>
            <Text style={styles.taskTitle}>{task.title}</Text>
            <View style={{ flexDirection: "row", gap: 8 }}>
              <MaterialCommunityIcons
                name={getPriorityIcon(task.priority)}
                size={20}
                color={getPriorityColor(task.priority)}
              />
              <TouchableOpacity onPress={() => onDelete(task.id)}>
                <MaterialCommunityIcons
                  name="delete"
                  size={20}
                  color="#EF4444"
                />
              </TouchableOpacity>
            </View>
          </View>

          <Text style={styles.taskDescription}>{task.description}</Text>

          {task.tags?.length ? (
            <View style={styles.tagsContainer}>
              {task.tags.map((tag, i) => (
                <View key={i} style={styles.tag}>
                  <Text style={styles.tagText}>{tag}</Text>
                </View>
              ))}
            </View>
          ) : null}

          <View style={styles.taskFooter}>
            {task.assignee && (
              <View style={styles.assigneeContainer}>
                <View style={styles.avatar}>
                  <Text style={styles.avatarText}>
                    {task.assignee
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </Text>
                </View>
                <Text style={styles.assigneeText}>{task.assignee}</Text>
              </View>
            )}

            {task.dueDate && (
              <View style={styles.dueDateContainer}>
                <MaterialCommunityIcons
                  name="calendar"
                  size={14}
                  color="#6B7280"
                />
                <Text style={styles.dueDateText}>{task.dueDate}</Text>
              </View>
            )}
          </View>
        </Pressable>
      )}
    </View>
  );
}

// Sortable Column
function SortableColumn({
  column,
  children,
  onEdit,
  onDelete,
  onAddTask,
}: {
  column: Column;
  children: React.ReactNode;
  onEdit: (column: Column) => void;
  onDelete: (columnId: string) => void;
  onAddTask: (columnId: string) => void;
}) {
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: column.id,
  });

  const {
    setNodeRef: setSortableRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: column.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <View
      ref={setSortableRef}
      style={[styles.column, isOver && styles.columnHighlighted, style as any]}
    >
      <View
        style={[styles.columnHeader, { backgroundColor: column.color }]}
        {...attributes}
        {...listeners}
      >
        <Text style={styles.columnTitle}>{column.title}</Text>
        <View style={{ flexDirection: "row", gap: 8, alignItems: "center" }}>
          <View style={styles.columnBadge}>
            <Text style={styles.columnBadgeText}>{column.tasks.length}</Text>
          </View>
          <TouchableOpacity onPress={() => onEdit(column)}>
            <MaterialCommunityIcons name="pencil" size={18} color="#fff" />
          </TouchableOpacity>
          <TouchableOpacity onPress={() => onDelete(column.id)}>
            <MaterialCommunityIcons name="delete" size={18} color="#fff" />
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView
        ref={setDroppableRef}
        style={styles.columnContent}
        showsVerticalScrollIndicator={false}
      >
        {children}
      </ScrollView>

      <TouchableOpacity
        style={styles.addTaskButton}
        onPress={() => onAddTask(column.id)}
      >
        <MaterialCommunityIcons name="plus" size={20} color="#3B82F6" />
        <Text style={styles.addTaskButtonText}>Add Task</Text>
      </TouchableOpacity>
    </View>
  );
}

// Task Form Modal
function TaskFormModal({
  visible,
  task,
  onSave,
  onClose,
}: {
  visible: boolean;
  task: Task | null;
  onSave: (task: Partial<Task>) => void;
  onClose: () => void;
}) {
  const [formData, setFormData] = useState<Partial<Task>>(
    task || {
      title: "",
      description: "",
      priority: "medium",
      assignee: "",
      tags: [],
      orderNumber: "",
      dueDate: "",
    },
  );

  const [tagInput, setTagInput] = useState("");

  React.useEffect(() => {
    if (task) {
      setFormData(task);
    } else {
      setFormData({
        title: "",
        description: "",
        priority: "medium",
        assignee: "",
        tags: [],
        orderNumber: "",
        dueDate: "",
      });
    }
  }, [task, visible]);

  const handleAddTag = () => {
    if (tagInput.trim()) {
      setFormData({
        ...formData,
        tags: [...(formData.tags || []), tagInput.trim()],
      });
      setTagInput("");
    }
  };

  const handleRemoveTag = (index: number) => {
    setFormData({
      ...formData,
      tags: formData.tags?.filter((_, i) => i !== index),
    });
  };

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <Text style={styles.modalTitle}>
            {task ? "Edit Task" : "New Task"}
          </Text>

          <ScrollView showsVerticalScrollIndicator={false}>
            <Text style={styles.label}>Title *</Text>
            <TextInput
              style={styles.input}
              value={formData.title}
              onChangeText={(text) => setFormData({ ...formData, title: text })}
              placeholder="Enter task title"
            />

            <Text style={styles.label}>Description</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.description}
              onChangeText={(text) =>
                setFormData({ ...formData, description: text })
              }
              placeholder="Enter description"
              multiline
              numberOfLines={3}
            />

            <Text style={styles.label}>Priority</Text>
            <View style={styles.priorityContainer}>
              {PRIORITY_OPTIONS.map((p) => (
                <TouchableOpacity
                  key={p}
                  style={[
                    styles.priorityButton,
                    formData.priority === p && styles.priorityButtonActive,
                    {
                      borderColor: getPriorityColor(p),
                      backgroundColor:
                        formData.priority === p
                          ? getPriorityColor(p)
                          : "transparent",
                    },
                  ]}
                  onPress={() =>
                    setFormData({
                      ...formData,
                      priority: p as Task["priority"],
                    })
                  }
                >
                  <Text
                    style={[
                      styles.priorityButtonText,
                      formData.priority === p &&
                        styles.priorityButtonTextActive,
                    ]}
                  >
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={styles.label}>Order Number</Text>
            <TextInput
              style={styles.input}
              value={formData.orderNumber}
              onChangeText={(text) =>
                setFormData({ ...formData, orderNumber: text })
              }
              placeholder="e.g., #12345"
            />

            <Text style={styles.label}>Assignee</Text>
            <TextInput
              style={styles.input}
              value={formData.assignee}
              onChangeText={(text) =>
                setFormData({ ...formData, assignee: text })
              }
              placeholder="Enter assignee name"
            />

            <Text style={styles.label}>Due Date</Text>
            <TextInput
              style={styles.input}
              value={formData.dueDate}
              onChangeText={(text) =>
                setFormData({ ...formData, dueDate: text })
              }
              placeholder="YYYY-MM-DD"
            />

            <Text style={styles.label}>Tags</Text>
            <View style={styles.tagInputContainer}>
              <TextInput
                style={[styles.input, { flex: 1 }]}
                value={tagInput}
                onChangeText={setTagInput}
                placeholder="Add tag"
                onSubmitEditing={handleAddTag}
              />
              <TouchableOpacity
                style={styles.addTagButton}
                onPress={handleAddTag}
              >
                <MaterialCommunityIcons name="plus" size={20} color="#fff" />
              </TouchableOpacity>
            </View>

            {formData.tags && formData.tags.length > 0 && (
              <View style={styles.tagsContainer}>
                {formData.tags.map((tag, i) => (
                  <View key={i} style={styles.tagWithDelete}>
                    <Text style={styles.tagText}>{tag}</Text>
                    <TouchableOpacity onPress={() => handleRemoveTag(i)}>
                      <MaterialCommunityIcons
                        name="close"
                        size={14}
                        color="#6B7280"
                      />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}
          </ScrollView>

          <View style={styles.modalActions}>
            <TouchableOpacity
              style={[styles.button, styles.buttonSecondary]}
              onPress={onClose}
            >
              <Text style={styles.buttonSecondaryText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.button, styles.buttonPrimary]}
              onPress={() => {
                if (formData.title?.trim()) {
                  onSave(formData);
                  onClose();
                }
              }}
            >
              <Text style={styles.buttonPrimaryText}>Save</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

// Column Form Modal
function ColumnFormModal({
  visible,
  column,
  onSave,
  onClose,
}: {
  visible: boolean;
  column: Column | null;
  onSave: (column: Partial<Column>) => void;
  onClose: () => void;
}) {
  const [formData, setFormData] = useState<Partial<Column>>(
    column || {
      title: "",
      color: "#3B82F6",
    },
  );

  const COLORS = [
    "#3B82F6",
    "#F59E0B",
    "#8B5CF6",
    "#10B981",
    "#EF4444",
    "#6B7280",
    "#EC4899",
    "#14B8A6",
  ];

  React.useEffect(() => {
    if (column) {
      setFormData(column);
    } else {
      setFormData({
        title: "",
        color: "#3B82F6",
      });
    }
  }, [column, visible]);

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={styles.modalOverlay}>
        <View style={[styles.modalContent, { width: 350 }]}>
          <Text style={styles.modalTitle}>
            {column ? "Edit Column" : "New Column"}
          </Text>

          <Text style={styles.label}>Column Title *</Text>
          <TextInput
            style={styles.input}
            value={formData.title}
            onChangeText={(text) => setFormData({ ...formData, title: text })}
            placeholder="Enter column title"
          />

          <Text style={styles.label}>Color</Text>
          <View style={styles.colorContainer}>
            {COLORS.map((color) => (
              <TouchableOpacity
                key={color}
                style={[
                  styles.colorButton,
                  { backgroundColor: color },
                  formData.color === color && styles.colorButtonActive,
                ]}
                onPress={() => setFormData({ ...formData, color })}
              >
                {formData.color === color && (
                  <MaterialCommunityIcons name="check" size={20} color="#fff" />
                )}
              </TouchableOpacity>
            ))}
          </View>

          <View style={styles.modalActions}>
            <TouchableOpacity
              style={[styles.button, styles.buttonSecondary]}
              onPress={onClose}
            >
              <Text style={styles.buttonSecondaryText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.button, styles.buttonPrimary]}
              onPress={() => {
                if (formData.title?.trim()) {
                  onSave(formData);
                  onClose();
                }
              }}
            >
              <Text style={styles.buttonPrimaryText}>Save</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

// Main Screen
export default function KanbanPOCScreen() {
  const [columns, setColumns] = useState<Column[]>([
    {
      id: "incoming",
      title: "Incoming Orders",
      color: "#3B82F6",
      tasks: [
        {
          id: "1",
          title: "Order #12345",
          description: "Customer: ABC Corp - 500 units",
          priority: "high",
          orderNumber: "#12345",
          assignee: "Poojan P.",
          tags: ["Urgent", "China"],
          dueDate: "2026-02-05",
        },
        {
          id: "2",
          title: "Order #12346",
          description: "Customer: XYZ Ltd - 300 units",
          priority: "medium",
          orderNumber: "#12346",
          assignee: "Sarah M.",
          tags: ["Standard"],
          dueDate: "2026-02-10",
        },
      ],
    },
    {
      id: "in-production",
      title: "In Production",
      color: "#F59E0B",
      tasks: [
        {
          id: "3",
          title: "Order #12340",
          description: "Customer: DEF Inc - 750 units",
          priority: "medium",
          orderNumber: "#12340",
          assignee: "Mike R.",
          tags: ["Line 3"],
          dueDate: "2026-02-08",
        },
      ],
    },
    {
      id: "quality-check",
      title: "Quality Check",
      color: "#8B5CF6",
      tasks: [
        {
          id: "4",
          title: "Order #12335",
          description: "Customer: GHI Group - 200 units",
          priority: "low",
          orderNumber: "#12335",
          assignee: "Emma L.",
          tags: ["QC"],
          dueDate: "2026-02-06",
        },
      ],
    },
    {
      id: "ready-to-ship",
      title: "Ready to Ship",
      color: "#10B981",
      tasks: [
        {
          id: "5",
          title: "Order #12330",
          description: "Customer: JKL Partners - 1000 units",
          priority: "critical",
          orderNumber: "#12330",
          assignee: "David K.",
          tags: ["Express"],
          dueDate: "2026-02-04",
        },
        {
          id: "6",
          title: "Order #12329",
          description: "Customer: MNO Corp - 450 units",
          priority: "medium",
          orderNumber: "#12329",
          assignee: "Lisa P.",
          tags: ["Standard"],
          dueDate: "2026-02-07",
        },
      ],
    },
    {
      id: "completed",
      title: "Completed",
      color: "#6B7280",
      tasks: [
        {
          id: "7",
          title: "Order #12320",
          description: "Customer: PQR Ltd - 600 units",
          priority: "low",
          orderNumber: "#12320",
          assignee: "Tom W.",
          tags: ["Delivered"],
          dueDate: "2026-02-01",
        },
      ],
    },
  ]);

  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [taskFormVisible, setTaskFormVisible] = useState(false);
  const [columnFormVisible, setColumnFormVisible] = useState(false);
  const [editingTask, setEditingTask] = useState<{
    task: Task;
    columnId: string;
  } | null>(null);
  const [editingColumn, setEditingColumn] = useState<Column | null>(null);
  const [addingTaskToColumn, setAddingTaskToColumn] = useState<string | null>(
    null,
  );

  const [activeId, setActiveId] = useState<string | null>(null);

  const findColumnByTaskId = (taskId: string) =>
    columns.find((c) => c.tasks.some((t) => t.id === taskId));

  const findTaskById = (taskId: string) => {
    for (const col of columns) {
      const task = col.tasks.find((t) => t.id === taskId);
      if (task) return task;
    }
    return null;
  };

  const moveTask = useCallback((fromId: string, toId: string, task: Task) => {
    if (fromId === toId) return;
    setColumns((prev) =>
      prev.map((col) => {
        if (col.id === fromId) {
          return { ...col, tasks: col.tasks.filter((t) => t.id !== task.id) };
        }
        if (col.id === toId) {
          return { ...col, tasks: [...col.tasks, task] };
        }
        return col;
      }),
    );
  }, []);

  // Task CRUD
  const handleAddTask = (columnId: string) => {
    setAddingTaskToColumn(columnId);
    setEditingTask(null);
    setTaskFormVisible(true);
  };

  const handleEditTask = (task: Task) => {
    const column = findColumnByTaskId(task.id);
    if (column) {
      setEditingTask({ task, columnId: column.id });
      setAddingTaskToColumn(null);
      setTaskFormVisible(true);
    }
  };

  const handleSaveTask = (taskData: Partial<Task>) => {
    if (editingTask) {
      // Edit existing task
      setColumns((prev) =>
        prev.map((col) => {
          if (col.id === editingTask.columnId) {
            return {
              ...col,
              tasks: col.tasks.map((t) =>
                t.id === editingTask.task.id ? { ...t, ...taskData } : t,
              ),
            };
          }
          return col;
        }),
      );
    } else if (addingTaskToColumn) {
      // Add new task
      const newTask: Task = {
        id: Date.now().toString(),
        title: taskData.title || "",
        description: taskData.description || "",
        priority: taskData.priority || "medium",
        assignee: taskData.assignee,
        tags: taskData.tags,
        orderNumber: taskData.orderNumber,
        dueDate: taskData.dueDate,
      };

      setColumns((prev) =>
        prev.map((col) => {
          if (col.id === addingTaskToColumn) {
            return { ...col, tasks: [...col.tasks, newTask] };
          }
          return col;
        }),
      );
    }
  };

  const handleDeleteTask = (taskId: string) => {
    setColumns((prev) =>
      prev.map((col) => ({
        ...col,
        tasks: col.tasks.filter((t) => t.id !== taskId),
      })),
    );
  };

  // Column CRUD
  const handleAddColumn = () => {
    setEditingColumn(null);
    setColumnFormVisible(true);
  };

  const handleEditColumn = (column: Column) => {
    setEditingColumn(column);
    setColumnFormVisible(true);
  };

  const handleSaveColumn = (columnData: Partial<Column>) => {
    if (editingColumn) {
      // Edit existing column
      setColumns((prev) =>
        prev.map((col) =>
          col.id === editingColumn.id ? { ...col, ...columnData } : col,
        ),
      );
    } else {
      // Add new column
      const newColumn: Column = {
        id: Date.now().toString(),
        title: columnData.title || "",
        color: columnData.color || "#3B82F6",
        tasks: [],
      };
      setColumns((prev) => [...prev, newColumn]);
    }
  };

  const handleDeleteColumn = (columnId: string) => {
    setColumns((prev) => prev.filter((col) => col.id !== columnId));
  };

  const handleDragStart = (event: any) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    // Check if dragging a column
    const activeColumnIndex = columns.findIndex((col) => col.id === activeId);
    const overColumnIndex = columns.findIndex((col) => col.id === overId);

    if (activeColumnIndex !== -1 && overColumnIndex !== -1) {
      // Reorder columns
      setColumns((prev) => arrayMove(prev, activeColumnIndex, overColumnIndex));
      return;
    }

    // Check if dragging a task
    const fromColumn = findColumnByTaskId(activeId);
    const toColumn = columns.find((c) => c.id === overId);
    const task = findTaskById(activeId);

    if (fromColumn && toColumn && task) {
      moveTask(fromColumn.id, toColumn.id, task);
    }
  };

  const activeTask = activeId ? findTaskById(activeId) : null;

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Kanban Board</Text>
        <TouchableOpacity style={styles.addButton} onPress={handleAddColumn}>
          <MaterialCommunityIcons name="plus" size={24} color="#fff" />
          <Text style={styles.addButtonText}>Add Column</Text>
        </TouchableOpacity>
      </View>

      <DndContext
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={columns.map((col) => col.id)}
          strategy={horizontalListSortingStrategy}
        >
          <ScrollView
            horizontal
            style={styles.board}
            showsHorizontalScrollIndicator={false}
          >
            {columns.map((column) => (
              <SortableColumn
                key={column.id}
                column={column}
                onEdit={handleEditColumn}
                onDelete={handleDeleteColumn}
                onAddTask={handleAddTask}
              >
                {column.tasks.map((task) => (
                  <DraggableTaskCard
                    key={task.id}
                    task={task}
                    onPress={handleEditTask}
                    onDelete={handleDeleteTask}
                  />
                ))}
              </SortableColumn>
            ))}
          </ScrollView>
        </SortableContext>

        <DragOverlay>
          {activeTask && (
            <View style={[styles.taskCard, { width: COLUMN_WIDTH - 32 }]}>
              <View style={styles.taskHeader}>
                <Text style={styles.taskTitle}>{activeTask.title}</Text>
                <MaterialCommunityIcons
                  name={getPriorityIcon(activeTask.priority)}
                  size={20}
                  color={getPriorityColor(activeTask.priority)}
                />
              </View>
              <Text style={styles.taskDescription}>
                {activeTask.description}
              </Text>
            </View>
          )}
        </DragOverlay>
      </DndContext>

      <TaskFormModal
        visible={taskFormVisible}
        task={editingTask?.task || null}
        onSave={handleSaveTask}
        onClose={() => setTaskFormVisible(false)}
      />

      <ColumnFormModal
        visible={columnFormVisible}
        column={editingColumn}
        onSave={handleSaveColumn}
        onClose={() => setColumnFormVisible(false)}
      />
    </View>
  );
}

// Styles
const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: "#F9FAFB" },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    backgroundColor: "#fff",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  headerTitle: { fontSize: 24, fontWeight: "700", color: "#111827" },
  addButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#3B82F6",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 8,
  },
  addButtonText: { color: "#fff", fontWeight: "600" },
  board: { flex: 1, padding: 16 },
  column: {
    width: COLUMN_WIDTH,
    backgroundColor: "#F3F4F6",
    borderRadius: 8,
    marginRight: 16,
    maxHeight: SCREEN_HEIGHT - 150,
  },
  columnHighlighted: {
    borderWidth: 2,
    borderColor: "#3B82F6",
    borderStyle: "dashed",
  },
  columnHeader: {
    padding: 12,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  columnTitle: { color: "#fff", fontWeight: "700", fontSize: 14, flex: 1 },
  columnBadge: {
    backgroundColor: "rgba(255,255,255,0.3)",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
  },
  columnBadgeText: { color: "#fff", fontWeight: "700", fontSize: 12 },
  columnContent: { padding: 8, flex: 1 },
  addTaskButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: 12,
    gap: 8,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  addTaskButtonText: { color: "#3B82F6", fontWeight: "600" },
  taskCard: {
    backgroundColor: "#fff",
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  taskCardDragging: { shadowOpacity: 0.3, shadowRadius: 8 },
  taskHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  taskTitle: { fontWeight: "600", flex: 1, fontSize: 14 },
  taskDescription: { color: "#6B7280", marginBottom: 8, fontSize: 13 },
  tagsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    marginBottom: 8,
  },
  tag: {
    backgroundColor: "#DBEAFE",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  tagText: { fontSize: 11, fontWeight: "600", color: "#1E40AF" },
  taskFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  assigneeContainer: { flexDirection: "row", gap: 6, alignItems: "center" },
  avatar: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: "#3B82F6",
    alignItems: "center",
    justifyContent: "center",
  },
  avatarText: { color: "#fff", fontSize: 10, fontWeight: "700" },
  assigneeText: { fontSize: 12, color: "#374151" },
  dueDateContainer: { flexDirection: "row", gap: 4, alignItems: "center" },
  dueDateText: { fontSize: 11, color: "#6B7280" },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "#fff",
    padding: 24,
    borderRadius: 12,
    width: SCREEN_WIDTH * 0.9,
    maxWidth: 500,
    maxHeight: SCREEN_HEIGHT * 0.8,
  },
  modalTitle: { fontSize: 20, fontWeight: "700", marginBottom: 20 },
  label: { fontSize: 14, fontWeight: "600", marginBottom: 8, color: "#374151" },
  input: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    fontSize: 14,
  },
  textArea: { minHeight: 80, textAlignVertical: "top" },
  priorityContainer: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  priorityButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 2,
  },
  priorityButtonActive: {},
  priorityButtonText: { fontWeight: "600", fontSize: 13 },
  priorityButtonTextActive: { color: "#fff" },
  tagInputContainer: { flexDirection: "row", gap: 8, marginBottom: 8 },
  addTagButton: {
    backgroundColor: "#3B82F6",
    padding: 12,
    borderRadius: 8,
    justifyContent: "center",
    alignItems: "center",
  },
  tagWithDelete: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#DBEAFE",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    gap: 4,
  },
  colorContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 16,
  },
  colorButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  colorButtonActive: {
    borderWidth: 3,
    borderColor: "#fff",
    shadowColor: "#000",
    shadowOpacity: 0.3,
    shadowRadius: 4,
  },
  modalActions: { flexDirection: "row", gap: 12, marginTop: 16 },
  button: { flex: 1, padding: 14, borderRadius: 8, alignItems: "center" },
  buttonPrimary: { backgroundColor: "#3B82F6" },
  buttonSecondary: { backgroundColor: "#E5E7EB" },
  buttonPrimaryText: { color: "#fff", fontWeight: "600", fontSize: 15 },
  buttonSecondaryText: { color: "#374151", fontWeight: "600", fontSize: 15 },
});
